import util from '../util';
import FetchPoller from '../FetchPoller';
import Widget from './Widget';

export default class ViewerCountWidget extends Widget {
	constructor(opts) {
		super(opts);
		this.chats = [];
		this._channelPoller = null;
		this._channelPollerStarted = false;
	}

	get _widgetType() { return 'viewer-count'; }

	async init() {
		this._channelPoller = new FetchPoller(signal =>
			this.api.get(`widget/${encodeURIComponent(this.hash)}/channels`, {}, signal).then(channels => {
				this._updateChannels(channels);
			}).catch(err => console.error('Channel polling error', err)),
			5 * 60 * 1000,
		);
		await super.init();
	}

	_build() {
		this.container.innerHTML = '<div class="viewer-count-widget"></div>';
		this._widgetEl = this.container.querySelector('.viewer-count-widget');
	}

	destroy() {
		if(this._channelPoller) {
			this._channelPoller.stop();
			for(const chat of this.chats)
				chat.poller?.stop();
			this._channelPoller = null;
		}
		document.getElementById(`viewerCountStyle-${this.container.id}`)?.remove();
		super.destroy();
	}

	applySettings(settings) {
		const old = this.settings;
		const needsChannelRefresh = !this._channelPollerStarted
			|| old.showDisabledChannels !== settings.showDisabledChannels
			|| old.showOfflineChannels !== settings.showOfflineChannels
			|| old.showOnlyVerifiedChannels !== settings.showOnlyVerifiedChannels;
		super.applySettings(settings);
		this.render();
		if(needsChannelRefresh && this._channelPoller) {
			this._channelPollerStarted = true;
			this._channelPoller.start();
		}
	}

	_socketHandlers() {
		return {
			channelInfoUpdate: message => {
				const chat = this.chats.find(c => c.service_id == message.serviceId && c.token == message.channelToken);
				if(chat && chat.info) {
					if('numViewers' in message) chat.info.numViewers = message.numViewers;
					if('numLive' in message) chat.info.numLive = message.numLive;
					this.render();
				}
			},
			channelCreated: () => this._channelPoller.start(),
			channelDeleted: () => this._channelPoller.start(),
			channelStatusChanged: () => this._channelPoller.start(),
		};
	}

	render() {
		if(!this._widgetEl) return;
		this._widgetEl.innerHTML = '';

		const s = this.settings;
		const logoOnRight = s.logoPosition === 'right';

		let chats = this.chats.filter(chat => s.showOfflineChannels || chat.info?.numLive > 0);
		if(!s.sort) chats = chats.slice().sort((a, b) => (a.service_name || '').localeCompare(b.service_name || '') || (a.name || '').localeCompare(b.name || ''));
		if(s.sort === 'asc')  chats = chats.slice().sort((a, b) => (a.info?.numViewers || 0) - (b.info?.numViewers || 0));
		if(s.sort === 'desc') chats = chats.slice().sort((a, b) => (b.info?.numViewers || 0) - (a.info?.numViewers || 0));

		for(const chat of chats) {
			const chatEl = document.createElement('span');
			chatEl.className = 'chat' + (chat.error ? ' error' : '') + (logoOnRight ? ' right' : '');
			chatEl.title = chat.name || '';

			if(!logoOnRight) {
				if(chat.service_icon_url) {
					const avatar = document.createElement('span');
					avatar.className = 'avatar';
					avatar.style.backgroundImage = `url(${chat.service_icon_url})`;
					chatEl.appendChild(avatar);
				} else {
					const b = document.createElement('b');
					b.textContent = `${chat.service_name}:`;
					chatEl.appendChild(b);
				}
			}

			if(chat.info && chat.info.numLive > 0) {
				const count = document.createElement('span');
				count.textContent = chat.info.numViewers === null ? '?' : (chat.info.numViewers || 0);
				chatEl.appendChild(count);
			} else {
				const i = document.createElement('i');
				i.textContent = chat.error ? 'error' : '×';
				chatEl.appendChild(i);
			}

			if(logoOnRight) {
				if(chat.service_icon_url) {
					const avatar = document.createElement('span');
					avatar.className = 'avatar';
					avatar.style.backgroundImage = `url(${chat.service_icon_url})`;
					chatEl.appendChild(avatar);
				} else {
					const b = document.createElement('b');
					b.textContent = `${chat.service_name}:`;
					chatEl.appendChild(b);
				}
			}

			this._widgetEl.appendChild(chatEl);
		}
	}

	_updateChannels(channels) {
		for(const c of channels) {
			const show = (this.settings.showDisabledChannels || c.status === 'enabled')
				&& (!this.settings.showOnlyVerifiedChannels || c.verified)
				&& !c.no_streaming
				&& !(c.flags?.includes('no-viewer-count'));
			const existingIdx = this.chats.findIndex(h => h.service_id === c.service_id && h.token === c.token);

			if(existingIdx >= 0) {
				const chat = this.chats[existingIdx];
				if(show) {
					if(c.status === 'enabled') {
						if(chat.poller.state === 'none') chat.poller.start();
					} else {
						chat.poller.stop();
						chat.info = null;
						this.render();
					}
				} else {
					chat.info = null;
					chat.poller.stop();
					this.chats.splice(existingIdx, 1);
					this.render();
				}
			} else if(existingIdx === -1 && show) {
				const chat = { ...c, poller: null, info: null };
				chat.poller = new FetchPoller(signal =>
					this.api.get(`widget/${encodeURIComponent(this.hash)}/channels/${encodeURIComponent(c.service_id)}/${encodeURIComponent(c.token)}/info`, {}, signal)
						.then(info => { chat.info = info; this.render(); })
						.catch(err => console.error('Error polling chat', c.service_id, c.token, err)),
					60 * 1000,
				);
				this.chats.push(chat);
				if(c.status === 'enabled') chat.poller.start();
			}
		}
		this.render();
	}

	_generateCSS() {
		const s = this.settings;
		const id = this.container.id;
		const isVertical = s.orientation === 'vertical';

		const css = `
			#${id} {
				font-family: Arial, sans-serif;
				font-size: 18px;
				color: white;
				margin: 0;
				padding: 0;
				width: 100%;
				height: 100%;
				${s.textColor   ? `color: ${s.textColor};` : ''}
				${s.fontFamily  ? `font-family: ${s.fontFamily};` : ''}
				${s.fontSize > 0 ? `font-size: ${s.fontSize}px; line-height: ${s.fontSize}px;` : ''}
				${s.letterSpacing > 0 ? `letter-spacing: ${s.letterSpacing}px;` : ''}
				${s.boldText    ? `font-weight: bold;` : ''}
				${s.italicText  ? `font-style: italic;` : ''}
			}
			#${id} .viewer-count-widget {
				display: flex;
				flex-wrap: wrap;
				flex-direction: ${isVertical ? 'column' : 'row'};
				justify-content: ${s.justifyContent || 'flex-start'};
				align-items: ${s.alignItems || 'center'};
				align-content: ${s.alignItems || 'center'};
				width: 100%;
				height: 100%;
			}
			#${id} .chat {
				padding: 4px;
				border-radius: 8px;
				background-color: rgba(0, 128, 0, 0);
				margin: 2px;
				${s.textBg ? `background-color: ${s.textBg};` : ''}
				${s.fontSize > 0 ? `padding: ${Math.ceil(s.fontSize/5)}px;` : ''}
				${s.fontSize > 0 ? `border-radius: ${Math.ceil(s.fontSize/2.5)}px;` : ''}
				${s.fontSize > 0 ? `margin: ${Math.ceil(s.fontSize/10)}px;` : ''}
				${s.underlineText || s.strikethroughText ? `text-decoration: ${s.underlineText ? 'underline' : ''} ${s.strikethroughText ? 'line-through' : ''};` : ''}
				${s.blurRadius > 0 || s.spreadRadius > 0 ? `filter: ${util.generateSpreadShadowFilter(s.spreadRadius, s.blurRadius, s.shadowColor)};` : ''}
			}
			#${id} .chat.error { background: red; }
			#${id} .chat .avatar {
				width: 16px;
				height: 16px;
				margin-right: 4px;
				vertical-align: top;
				display: inline-block;
				background-size: cover;
				${s.fontSize > 0 ? `width: ${s.fontSize}px; height: ${s.fontSize}px; margin-right: ${Math.ceil(s.fontSize/5)}px;` : ''}
			}
			#${id} .chat.right .avatar {
				margin-right: 0;
				margin-left: 4px;
				${s.fontSize > 0 ? `margin-left: ${Math.ceil(s.fontSize/5)}px;` : ''}
			}
			${s.extraCSS || ''}
		`;

		this._setCSS(`viewerCountStyle-${id}`, css);
		this._updateFontLink();
	}
}
